import React, { useEffect, useState, useRef } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, XMarkIcon, EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { environment } from "./environment";
import { useAuth } from "./App";
import { useNavigate } from 'react-router-dom';

const API_URL = `${environment.apiUrl}/api/Clients`;
const initialForm = { first_name: '', last_name: '', date_of_birth: '', phone: '', email: '', address: '', history: '', collateral_first_name: '', collateral_last_name: '', collateral_email: '' };

function ClientModal({ open, onClose, onSave, form, setForm, loading, error, isEdit }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm relative max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-4 pb-2">
          <h2 className="text-lg font-bold">{isEdit ? 'Edit Client' : 'New Client'}</h2>
          <button
            className="text-slate-400 hover:text-slate-700"
            onClick={onClose}
            aria-label="Close"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="px-4 overflow-y-auto flex-1">
          <form
            id="client-form"
            onSubmit={e => {
              e.preventDefault();
              onSave();
            }}
            className="flex flex-col gap-1.5 pb-2"
          >
            <div>
              <label className="block mb-1 font-medium text-sm">First Name</label>
              <input
                className="w-full border border-slate-300 rounded-lg px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                value={form.first_name}
                onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))}
                required
                maxLength={100}
              />
            </div>
            <div>
              <label className="block mb-1 font-medium text-sm">Last Name</label>
              <input
                className="w-full border border-slate-300 rounded-lg px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                value={form.last_name}
                onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))}
                required
                maxLength={100}
              />
            </div>
            <div>
              <label className="block mb-1 font-medium text-sm">Date of Birth</label>
              <input
                className="w-full border border-slate-300 rounded-lg px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                type="date"
                value={form.date_of_birth}
                onChange={e => setForm(f => ({ ...f, date_of_birth: e.target.value }))}
                required
              />
            </div>
            <div>
              <label className="block mb-1 font-medium text-sm">Phone</label>
              <input
                className="w-full border border-slate-300 rounded-lg px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                value={form.phone}
                onChange={e => {
                  // Only allow numbers
                  const val = e.target.value.replace(/[^0-9]/g, '');
                  setForm(f => ({ ...f, phone: val }));
                }}
                maxLength={20}
                inputMode="numeric"
                pattern="[0-9]*"
              />
            </div>
            <div>
              <label className="block mb-1 font-medium text-sm">Email</label>
              <input
                className="w-full border border-slate-300 rounded-lg px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                type="email"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                maxLength={255}
              />
            </div>
            <div>
              <label className="block mb-1 font-medium text-sm">Address</label>
              <textarea
                className="w-full border border-slate-300 rounded-lg px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-400 resize-y min-h-[50px] text-sm"
                value={form.address}
                onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                maxLength={500}
              />
            </div>
            <div>
              <label className="block mb-1 font-medium text-sm">History</label>
              <textarea
                className="w-full border border-slate-300 rounded-lg px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-400 resize-y min-h-[60px] text-sm"
                value={form.history}
                onChange={e => setForm(f => ({ ...f, history: e.target.value }))}
                maxLength={2000}
                placeholder="Enter client's medical/clinical history..."
              />
            </div>
            <div className="mt-2 mb-1 border-t pt-2">
              <div className="font-bold mb-2 text-slate-700 text-center text-sm">Collateral</div>
              <div className="mb-1">
                <label className="block mb-1 font-medium text-sm">First Name</label>
                <input
                  className="w-full border border-slate-300 rounded-lg px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                  value={form.collateral_first_name}
                  onChange={e => setForm(f => ({ ...f, collateral_first_name: e.target.value }))}
                  maxLength={100}
                />
              </div>
              <div className="mb-1">
                <label className="block mb-1 font-medium text-sm">Last Name</label>
                <input
                  className="w-full border border-slate-300 rounded-lg px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                  value={form.collateral_last_name}
                  onChange={e => setForm(f => ({ ...f, collateral_last_name: e.target.value }))}
                  maxLength={100}
                />
              </div>
              <div>
                <label className="block mb-1 font-medium text-sm">Email</label>
                <input
                  className="w-full border border-slate-300 rounded-lg px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                  type="email"
                  value={form.collateral_email}
                  onChange={e => setForm(f => ({ ...f, collateral_email: e.target.value }))}
                  maxLength={255}
                />
              </div>
            </div>
          </form>
        </div>
        <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
          {error && <div className="text-red-500 text-sm text-center mb-3">{error}</div>}
          <button
            type="submit"
            form="client-form"
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 rounded-lg transition disabled:opacity-60"
            disabled={loading}
            onClick={e => {
              e.preventDefault();
              onSave();
            }}
          >
            {loading ? (isEdit ? 'Saving...' : 'Adding...') : (isEdit ? 'Save Changes' : 'Add Client')}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmModal({ open, onClose, onConfirm, loading }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-sm relative flex flex-col items-center">
        <h2 className="text-xl font-bold mb-4">Delete Client?</h2>
        <p className="mb-6 text-center">Are you sure you want to delete this Client? This action cannot be undone.</p>
        <div className="flex gap-4">
          <button
            className="bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold py-2 px-5 rounded-lg"
            onClick={onClose}
            disabled={loading}
          >Cancel</button>
          <button
            className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-5 rounded-lg"
            onClick={onConfirm}
            disabled={loading}
          >{loading ? 'Deleting...' : 'Delete'}</button>
        </div>
      </div>
    </div>
  );
}

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  // For date-only strings like "2025-06-01", parse as local date to avoid timezone issues
  const d = new Date(dateStr + 'T00:00:00');
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
};

// Helper to get token from localStorage
function getToken() {
  return localStorage.getItem("token");
}

const Clients = () => {
  const navigate = useNavigate();
  const [Clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [formError, setFormError] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [apiError, setApiError] = useState('');
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const ClientsPerPage = 12;
  const [menuOpenId, setMenuOpenId] = useState(null);
  const menuRefs = useRef({});

  useEffect(() => {
    if (menuOpenId === null) return;
    function handleClickOutside(event) {
      const menu = menuRefs.current[menuOpenId];
      if (menu && !menu.contains(event.target)) {
        setMenuOpenId(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpenId]);

  // Fetch Clients
  useEffect(() => {
    fetchClients();
    // eslint-disable-next-line
  }, []);

  const fetchClients = async () => {
    setLoading(true);
    setApiError("");
    try {
      const res = await fetch(API_URL, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      const data = await res.json();
      console.log("GET /Clients response:", data);
      if (!res.ok) throw new Error("Failed to fetch Clients");
      setClients(data);
    } catch (err) {
      setApiError("Could not load Clients from the server.");
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  const openNewModal = () => {
    setForm(initialForm);
    setEditId(null);
    setFormError('');
    setModalOpen(true);
  };

  const openEditModal = (Client) => {
    setForm({
      first_name: Client.first_name || '',
      last_name: Client.last_name || '',
      date_of_birth: Client.date_of_birth || '',
      phone: Client.phone || '',
      email: Client.email || '',
      address: Client.address || '',
      history: Client.history || '',
      collateral_first_name: Client.collateral_first_name || '',
      collateral_last_name: Client.collateral_last_name || '',
      collateral_email: Client.collateral_email || '',
    });
    setEditId(Client.id);
    setFormError('');
    setModalOpen(true);
  };

  const handleSave = async () => {
    setFormError("");
    setModalLoading(true);
    // Validation
    if (!form.first_name.trim() || !form.last_name.trim() || !form.date_of_birth) {
      setFormError("First Name, Last Name, and Date of Birth are required.");
      setModalLoading(false);
      return;
    }
    // Convert date_of_birth to ISO datetime string
    const dobDate = form.date_of_birth ? new Date(form.date_of_birth) : null;
    const dobIso = dobDate ? dobDate.toISOString().split('T')[0] + 'T00:00:00' : '';
    const formToSend = {
      ...form,
      date_of_birth: dobIso,
      phone: form.phone.trim() === '' ? null : form.phone,
      email: form.email.trim() === '' ? null : form.email,
      address: form.address.trim() === '' ? null : form.address,
      history: form.history.trim() === '' ? null : form.history,
      collateral_first_name: form.collateral_first_name.trim() === '' ? null : form.collateral_first_name,
      collateral_last_name: form.collateral_last_name.trim() === '' ? null : form.collateral_last_name,
      collateral_email: form.collateral_email.trim() === '' ? null : form.collateral_email,
    };
    let res, data;
    try {
      if (editId) {
        // Edit
        res = await fetch(`${API_URL}/${editId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify(formToSend),
        });
        data = await res.json().catch(() => ({}));
        console.log("PUT /Clients/", editId, "response:", data);
        if (!res.ok) throw new Error("Failed to update Client");
        setSuccessMsg("Client updated!");
      } else {
        // New
        res = await fetch(API_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify(formToSend),
        });
        data = await res.json().catch(() => ({}));
        console.log("POST /Clients response:", data);
        if (!res.ok) throw new Error("Failed to add Client");
        setSuccessMsg("Client added!");
      }
      setModalOpen(false);
      await fetchClients();
    } catch (err) {
      setFormError(err.message || "Error saving Client");
    } finally {
      setModalLoading(false);
      setTimeout(() => setSuccessMsg(""), 2000);
    }
  };

  const openDeleteModal = (id) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      const res = await fetch(`${API_URL}/${deleteId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      const data = await res.json().catch(() => ({}));
      console.log("DELETE /Clients/", deleteId, "response:", data);
      if (!res.ok) throw new Error("Failed to delete Client");
      setSuccessMsg("Client deleted!");
      setConfirmOpen(false);
      await fetchClients();
    } catch (err) {
      setSuccessMsg("Error deleting Client");
    } finally {
      setDeleteLoading(false);
      setTimeout(() => setSuccessMsg(""), 2000);
    }
  };

  // Filter Clients by search
  const filteredClients = Clients.filter(p => {
    const searchTerm = search.toLowerCase();
    return (
      (p.first_name || '').toLowerCase().includes(searchTerm) ||
      (p.last_name || '').toLowerCase().includes(searchTerm) ||
      (p.phone || '').toLowerCase().includes(searchTerm) ||
      (p.email || '').toLowerCase().includes(searchTerm) ||
      (p.address || '').toLowerCase().includes(searchTerm) ||
      (p.collateral_first_name || '').toLowerCase().includes(searchTerm) ||
      (p.collateral_last_name || '').toLowerCase().includes(searchTerm) ||
      (p.collateral_email || '').toLowerCase().includes(searchTerm)
    );
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredClients.length / ClientsPerPage);
  const paginatedClients = filteredClients.slice(
    (currentPage - 1) * ClientsPerPage,
    currentPage * ClientsPerPage
  );

  return (
    <div className="w-full min-h-screen flex flex-col bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-0">
      <div className="w-full flex flex-col p-6">
        {/* Header */}
        <div className="flex w-full items-center justify-between mb-6">
          <div className="flex flex-col flex-1">
            <h2 className="text-3xl font-bold text-slate-800 mb-1">Clients</h2>
            <p className="text-slate-500 mb-3">Manage your client information</p>
            <div className="relative">
              <input
                type="text"
                placeholder="Search Clients by name..."
                value={search}
                onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
                className="w-80 border border-slate-300 rounded-xl pl-10 pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white shadow-sm"
              />
              <span className="absolute left-3 top-2.5 text-slate-400">🔍</span>
            </div>
          </div>
          <button
            className="flex items-center gap-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold px-6 py-2 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-0.5 ml-4"
            onClick={openNewModal}
          >
            <PlusIcon className="w-5 h-5" /> New Client
          </button>
        </div>
        {successMsg && <div className="text-green-600 mb-4">{successMsg}</div>}
        {apiError && <div className="text-red-600 mb-4">{apiError}</div>}
        {loading ? (
          <div className="text-slate-500">Loading Clients...</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-6 w-full">
            {paginatedClients.map((p) => {
              const initials = `${(p.first_name?.[0] || '').toUpperCase()}${(p.last_name?.[0] || '').toUpperCase()}`;
              return (
                <div
                  key={p.id}
                  className="relative bg-white rounded-2xl shadow-lg p-3 max-w-xs flex flex-col items-center min-h-[160px] h-full border border-slate-200 group transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl cursor-pointer bg-gradient-to-br from-white to-slate-50"
                  onClick={() => navigate(`/Clients/${p.id}`)}
                >
                  {/* 3-dots menu */}
                  <div className="absolute top-4 right-4 z-10" ref={el => menuRefs.current[p.id] = el}>
                    <button
                      className="p-2 rounded-full bg-white/80 backdrop-blur-sm hover:bg-blue-100 shadow-lg transition-all duration-200 border border-slate-200"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuOpenId(menuOpenId === p.id ? null : p.id);
                      }}
                      title="More options"
                    >
                      <EllipsisVerticalIcon className="w-5 h-5 text-slate-600" />
                    </button>
                    {menuOpenId === p.id && (
                      <div className="absolute right-0 mt-2 w-32 bg-white border border-slate-200 rounded-xl shadow-2xl py-2 z-50">
                        <button
                          className="w-full text-left px-4 py-2 text-sm hover:bg-blue-50 text-blue-700 flex items-center gap-2 transition-colors"
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            setMenuOpenId(null); 
                            openEditModal(p); 
                          }}
                        >
                          <PencilIcon className="w-4 h-4" /> Edit
                        </button>
                        <button
                          className="w-full text-left px-4 py-2 text-sm hover:bg-red-50 text-red-600 flex items-center gap-2 transition-colors"
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            setMenuOpenId(null); 
                            openDeleteModal(p.id); 
                          }}
                        >
                          <TrashIcon className="w-4 h-4" /> Delete
                        </button>
                      </div>
                    )}
                  </div>
                  {/* Avatar/Initial bubble or default profile image */}
                  {p.profile_image ? (
                    <img src={p.profile_image} alt="Profile" className="w-14 h-14 rounded-full object-cover mb-2 shadow-lg border-2 border-white" />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-extrabold text-lg shadow-lg border-2 border-white">
                      {initials}
                    </div>
                  )}
                  {/* Name */}
                  <div className="font-bold text-base text-slate-900 mb-1 text-center tracking-tight">
                    {p.first_name} {p.last_name}
                  </div>
                  {/* Details */}
                  <div className="flex flex-col gap-1 w-full mt-1">
                    <div className="flex items-center gap-2 bg-slate-50 rounded-lg p-1.5">
                      <span className="text-xs text-slate-500 font-medium min-w-[70px]">📅 DOB</span>
                      <span className="text-sm text-slate-700 font-medium">{formatDate(p.date_of_birth)}</span>
                    </div>
                    <div className="flex items-center gap-2 bg-slate-50 rounded-lg p-1.5">
                      <span className="text-xs text-slate-500 font-medium min-w-[70px]">📞 Phone</span>
                      <span className="text-sm text-slate-700 font-medium">{p.phone || ''}</span>
                    </div>
                    <div className="flex items-start gap-2 bg-slate-50 rounded-lg p-1.5">
                      <span className="text-xs text-slate-500 font-medium min-w-[70px] flex-shrink-0">✉️ Email</span>
                      <span className="text-sm text-slate-700 font-medium truncate max-w-[140px]" title={p.email}>
                        {p.email || ''}
                      </span>
                    </div>
                    <div className="flex items-start gap-2 bg-slate-50 rounded-lg p-1.5">
                      <span className="text-xs text-slate-500 font-medium min-w-[70px] flex-shrink-0">📍 Address</span>
                      <span className="text-sm text-slate-700 font-medium break-words" title={p.address}>{p.address || ''}</span>
                    </div>
                    {p.history && (
                      <div className="flex items-center gap-2 bg-blue-50 rounded-lg p-1.5">
                        <span className="text-xs text-blue-600 font-medium min-w-[70px]">📋 History</span>
                        <span className="text-sm text-blue-700 font-medium">Available</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex gap-4 mt-6 mb-4 items-center justify-center">
            <button
              className="px-4 py-2 rounded-lg bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold transition-all duration-200 shadow-sm hover:shadow-md"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              ← Prev
            </button>
            <span className="text-slate-700 font-medium px-4 py-2 bg-white rounded-lg border border-slate-200 shadow-sm">
              Page {currentPage} of {totalPages}
            </span>
            <button
              className="px-4 py-2 rounded-lg bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold transition-all duration-200 shadow-sm hover:shadow-md"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next →
            </button>
          </div>
        )}
      </div>
      <ClientModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        form={form}
        setForm={setForm}
        loading={modalLoading}
        error={formError}
        isEdit={!!editId}
      />
      <ConfirmModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
        loading={deleteLoading}
      />
    </div>
  );
};

export default Clients; 